/**
 * MigrationModeBanner (Iter 80.3)
 *
 * Persistent top-of-app banner that surfaces the tenant's Migration
 * Mode state to the audience that legitimately needs to see it.
 *
 * OBS-01 (2026-02 · Phase 1 Stabilisation Batch) — visibility gates:
 *
 *   - Banner is rendered ONLY when the caller is either the Primary
 *     Super Admin (`user.is_primary_admin === true`) OR carries the
 *     `leave:admin` capability (HR Leave Admin). Every other role
 *     (Sales / DATA_SALES / Developer / Viewer / Accounts /
 *     Operations / Support / Employee / linked-Manager without
 *     leave:admin) sees NOTHING — cross-FY audit tagging is not
 *     their business.
 *
 *   - "Credit Opening Leave Balances" CTA is rendered for both
 *     Primary Super Admin AND HR Leave Admin (they are the only two
 *     personas that can actually credit opening balances).
 *
 *   - "Manage" CTA (`/admin/migration-mode`) is rendered ONLY for
 *     the Primary Super Admin (the sole persona authorised to
 *     activate / sign-off Migration Mode per Iter 80.3 governance).
 *
 * Hidden on:
 *  - state == "not_started" (the default operational state)
 *  - state == "signed_off"  (terminal — no banner clutter post-migration)
 *  - print views (controlled via `.print:hidden` Tailwind class)
 *
 * Polls the tenant status every 30s so the banner is reactive to
 * sign-off from any session without requiring page reload.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, Sparkle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";

export default function MigrationModeBanner() {
    const [status, setStatus] = useState(null);
    const { user, can } = useAuth();

    // OBS-01 — Restrict banner visibility to the personas that
    // actually need to see the Migration Mode context: the Primary
    // Super Admin (activation / sign-off authority) and any user
    // holding `leave:admin` (HR Leave Admin, who executes the
    // Migration-Mode-primary opening-balance credits).
    const isPrimaryAdmin = user?.is_primary_admin === true;
    const isLeaveAdmin = typeof can === "function" ? can("leave:admin") : false;
    const canSeeBanner = isPrimaryAdmin || isLeaveAdmin;
    // Only the Primary Super Admin can reach /admin/migration-mode
    // (governance lock); everyone else's Manage link would 403.
    const canSeeManage = isPrimaryAdmin;
    // Both personas can credit opening balances (leave:admin is the
    // capability that gates the credit endpoint itself).
    const canCreditOpening = isPrimaryAdmin || isLeaveAdmin;

    useEffect(() => {
        if (!canSeeBanner) return undefined;
        let alive = true;
        const fetchStatus = async () => {
            try {
                const { data } = await api.get("/billing/migration-mode/status");
                if (alive) setStatus(data);
            } catch {
                // silent — banner stays hidden if status endpoint can't be reached
            }
        };
        fetchStatus();
        const t = setInterval(fetchStatus, 30000);
        return () => { alive = false; clearInterval(t); };
    }, [canSeeBanner]);

    if (!canSeeBanner) return null;
    if (!status || status.state !== "active") return null;

    return (
        <div
            className="print:hidden bg-rose-700 text-rose-50 border-b border-rose-800 shadow-sm"
            data-testid="migration-mode-banner"
        >
            <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3 text-xs">
                <Sparkle size={16} weight="fill" className="text-rose-200 shrink-0" />
                <span className="font-semibold uppercase tracking-wider text-[10px] bg-rose-900/40 px-1.5 py-0.5 rounded border border-rose-500/40">
                    Migration Mode
                </span>
                <span className="opacity-95">
                    Active since <b>{formatDate(status.activated_at)}</b>
                    {status.activated_by_email && (
                        <> · activated by <b>{status.activated_by_email}</b></>
                    )}
                    {status.activation_reason && (
                        <> · {status.activation_reason}</>
                    )}
                </span>
                <span className="opacity-80 hidden md:inline">
                    Cross-FY date edits are permitted and audit-tagged.
                </span>
                <span className="grow" />
                {canCreditOpening && (
                    <Link
                        to="/admin/leave/balances?include_pending=1"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-500/90 text-white border border-amber-400 hover:bg-amber-400 transition-colors"
                        data-testid="migration-mode-banner-opening-balance-link"
                    >
                        <Sparkle size={12} weight="fill" /> Credit Opening Leave Balances
                    </Link>
                )}
                {canSeeManage && (
                    <Link
                        to="/admin/migration-mode"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-900/40 border border-rose-500/40 hover:bg-rose-900/60 transition-colors"
                        data-testid="migration-mode-banner-manage-link"
                    >
                        <Database size={12} weight="fill" /> Manage
                    </Link>
                )}
            </div>
        </div>
    );
}
